import { resolveTheme, type CompiledCatalog, type ResolvedTheme, type Template, type Design } from "@baze/catalog";
import type { CatalogDesign, CatalogResponse, CatalogTemplate, SitePlan } from "@baze/api-client";
import type { WebsiteDefinition } from "@baze/schema";

type ThemeCatalog = Pick<CompiledCatalog, "designs" | "fonts" | "fontPairs">;

/** CatalogResponse items omit authoring-only fields; the resolver/instantiator never read them. */
export function asThemeCatalog(catalog: Pick<CatalogResponse, "designs" | "fonts" | "fontPairs">): ThemeCatalog {
  return catalog as unknown as ThemeCatalog;
}
export const asDesign = (d: CatalogDesign) => d as unknown as Design;
export const asTemplate = (t: CatalogTemplate) => t as unknown as Template;

const themeCache = new WeakMap<object, Map<string, ResolvedTheme | null>>();

/** Memoized resolveTheme per catalog object + theme ref. */
export function themeFor(catalog: Pick<CatalogResponse, "designs" | "fonts" | "fontPairs">, theme: WebsiteDefinition["theme"]): ResolvedTheme | null {
  let map = themeCache.get(catalog);
  if (!map) {
    map = new Map();
    themeCache.set(catalog, map);
  }
  const key = `${theme.id}@${theme.version}|${theme.overrides?.primary ?? ""}|${theme.overrides?.fontPair ?? ""}`;
  if (!map.has(key)) map.set(key, resolveTheme(asThemeCatalog(catalog), theme));
  return map.get(key) ?? null;
}

export function sortByOverlay<T extends { sort: number; featured: boolean; name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => Number(b.featured) - Number(a.featured) || a.sort - b.sort || a.name.localeCompare(b.name, "id"));
}

export function templatesForIndustry(catalog: CatalogResponse, industry: string): CatalogTemplate[] {
  return sortByOverlay(catalog.templates.filter((t) => t.industry === industry && t.status === "active"));
}

export function activeDesigns(catalog: CatalogResponse): CatalogDesign[] {
  return sortByOverlay(catalog.designs.filter((d) => d.status === "active"));
}

/** Recommended designs (template.designs order) first, then the rest. */
export function designsForTemplate(catalog: CatalogResponse, template: CatalogTemplate | undefined): { recommended: CatalogDesign[]; others: CatalogDesign[] } {
  const all = activeDesigns(catalog);
  if (!template) return { recommended: [], others: all };
  const rec = template.designs.map((id) => all.find((d) => d.id === id)).filter((d): d is CatalogDesign => !!d);
  const recIds = new Set(rec.map((d) => d.id));
  return { recommended: rec, others: all.filter((d) => !recIds.has(d.id)) };
}

export function findDesign(catalog: CatalogResponse | undefined, id: string | undefined): CatalogDesign | undefined {
  return id ? catalog?.designs.find((d) => d.id === id) : undefined;
}
export function findTemplate(catalog: CatalogResponse | undefined, id: string | undefined): CatalogTemplate | undefined {
  return id ? catalog?.templates.find((t) => t.id === id) : undefined;
}
export function industryName(catalog: CatalogResponse | undefined, id: string): string {
  return catalog?.industries.find((i) => i.id === id)?.name ?? id;
}

export function paidPlans(catalog: CatalogResponse | undefined): SitePlan[] {
  return [...(catalog?.sitePlans ?? [])].filter((p) => p.priceYear > 0).sort((a, b) => a.sort - b.sort);
}
/** The plan to suggest for an upsell: highlighted paid plan that satisfies `need`, else cheapest that does. */
export function recommendedPlan(catalog: CatalogResponse | undefined, need?: (p: SitePlan) => boolean): SitePlan | undefined {
  const plans = paidPlans(catalog).filter((p) => (need ? need(p) : true));
  return plans.find((p) => p.highlighted) ?? plans[0];
}
